import { useEffect, useMemo, useState } from "react";
import { X, ChevronLeft, Search, Link as LinkIcon, Check } from "lucide-react";
import toast from "react-hot-toast";
import { dictionary, Language } from "../../../utils/dictionary";
import { AdTableRow, assignProductToAd } from "../../../services/meta/metaService";
import { Product } from "../../products/ProductInterface";
import ThumbnailWithPlaceholder from "./ThumbnailWithPlaceholder";

interface AssignmentInboxModalProps {
  unassignedAds: AdTableRow[];
  products: Product[];
  language: Language;
  onClose: () => void;
  onAssigned: (adId: string, productId: string) => void;
}

export const AssignmentInboxModal = ({
  unassignedAds,
  products,
  language,
  onClose,
  onAssigned,
}: AssignmentInboxModalProps) => {
  const [cursor, setCursor] = useState(0);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [saving, setSaving] = useState(false);

  const total = unassignedAds.length;
  const currentAd = unassignedAds[cursor];

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  const filteredProducts = useMemo(() => {
    if (!query) return products;
    const q = query.toLowerCase();
    return products.filter((p) => p.name.toLowerCase().includes(q));
  }, [products, query]);

  if (total === 0 || !currentAd) {
    return (
      <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={onClose}>
        <div className="bg-white rounded-2xl shadow-2xl p-8 max-w-md w-full text-center" onClick={(e) => e.stopPropagation()}>
          <div className="mx-auto w-14 h-14 rounded-full bg-emerald-50 flex items-center justify-center mb-3">
            <Check className="w-7 h-7 text-emerald-500" />
          </div>
          <p className="text-sm font-semibold text-gray-900">
            {dictionary["assign-modal-all-done"]?.[language] ?? "Todos asignados"}
          </p>
          <button
            type="button"
            onClick={onClose}
            className="mt-4 h-9 px-4 text-sm font-medium bg-gray-900 text-white rounded-lg hover:opacity-90"
          >
            {dictionary["common-close"]?.[language] ?? "Cerrar"}
          </button>
        </div>
      </div>
    );
  }

  const handleAssign = async (productId: string) => {
    if (saving) return;
    setSaving(true);
    try {
      await assignProductToAd(currentAd.ad_id, productId);
      onAssigned(currentAd.ad_id, productId);
      if (cursor < total - 1) {
        setCursor(cursor + 1);
        setPickerOpen(false);
        setQuery("");
      } else {
        toast.success(dictionary["assign-modal-all-done"]?.[language] ?? "Todos asignados");
        onClose();
      }
    } catch {
      toast.error(dictionary["assign-modal-save-error"]?.[language] ?? "No pudimos guardar");
    } finally {
      setSaving(false);
    }
  };

  const handleSkip = () => {
    if (cursor < total - 1) {
      setCursor(cursor + 1);
      setPickerOpen(false);
      setQuery("");
    } else {
      onClose();
    }
  };

  const handleBack = () => {
    if (cursor > 0) {
      setCursor(cursor - 1);
      setPickerOpen(false);
      setQuery("");
    }
  };

  const progressPct = ((cursor + 1) / total) * 100;

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={onClose}>
      <div
        className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] flex flex-col overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 h-14 bg-gray-50 border-b border-gray-200">
          <div className="flex items-center gap-3">
            <span className="inline-flex items-center justify-center px-2 h-6 rounded-full bg-white border border-gray-200 text-[11px] font-semibold text-gray-700">
              {cursor + 1} / {total}
            </span>
            <div>
              <p className="text-sm font-semibold text-gray-900">
                {dictionary["assign-modal-title"]?.[language] ?? "Asignar anuncios"}
              </p>
              <p className="text-[11px] text-gray-500">
                {dictionary["assign-modal-subtitle"]?.[language] ?? "Revisá cada anuncio y elegí el producto"}
              </p>
            </div>
          </div>
          <button type="button" onClick={onClose} className="p-2 rounded-lg hover:bg-gray-100" aria-label="Close">
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        {/* Progress */}
        <div className="h-1 bg-gray-100">
          <div className="h-full bg-fluxi transition-all duration-300" style={{ width: `${progressPct}%` }} />
        </div>

        {/* Body */}
        <div className="flex-1 flex overflow-hidden">
          {/* Left: Ad preview */}
          <div className="w-2/5 bg-gray-50 border-r border-gray-200 p-5 overflow-y-auto">
            <p className="text-[10px] uppercase tracking-wide text-gray-400 font-semibold mb-2">
              {dictionary["campaigns-col-ad"]?.[language] ?? "Anuncio"}
            </p>
            <ThumbnailWithPlaceholder
              src={currentAd.thumbnail_url}
              alt={currentAd.ad_name}
              format={currentAd.format}
              className="rounded-lg mb-3"
              style={{ aspectRatio: "4 / 5" }}
              iconClass="w-10 h-10"
            />
            <p className="text-sm font-semibold text-gray-900 mb-1">{currentAd.ad_name}</p>
            {currentAd.destination_url && (
              <p className="text-[11px] text-gray-500 truncate flex items-center gap-1">
                <LinkIcon className="w-3 h-3 flex-shrink-0" />
                <span className="truncate">{currentAd.destination_url}</span>
              </p>
            )}
          </div>

          {/* Right: Picker */}
          <div className="flex-1 p-6 overflow-y-auto">
            <p className="text-[10px] uppercase tracking-wide text-gray-400 font-semibold mb-1">
              {dictionary["performance-unassigned-title"]?.[language] ?? "Sin asignar"}
            </p>
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              {dictionary["assign-modal-question"]?.[language] ?? "¿A qué producto pertenece?"}
            </h3>

            {!pickerOpen && (
              <button
                type="button"
                onClick={() => setPickerOpen(true)}
                className="w-full h-11 px-4 border border-gray-200 rounded-lg text-sm text-gray-700 hover:bg-gray-50 text-left"
              >
                {dictionary["performance-search-placeholder"]?.[language] ?? "Buscar productos…"}
              </button>
            )}

            {pickerOpen && (
              <div className="border border-gray-200 rounded-xl overflow-hidden">
                <div className="p-2 border-b border-gray-100">
                  <div className="relative">
                    <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <input
                      autoFocus
                      placeholder={dictionary["performance-search-placeholder"]?.[language] ?? "Buscar…"}
                      value={query}
                      onChange={(e) => setQuery(e.target.value)}
                      className="w-full h-9 pl-8 pr-2 text-sm border border-gray-200 rounded-md focus:outline-none focus:border-fluxi"
                    />
                  </div>
                </div>
                <div className="max-h-80 overflow-y-auto">
                  {filteredProducts.length === 0 && (
                    <p className="text-xs text-gray-400 text-center py-6">
                      {dictionary["assign-modal-empty-picker"]?.[language] ?? "Sin resultados"}
                    </p>
                  )}
                  {filteredProducts.map((p) => (
                    <button
                      key={p.id}
                      type="button"
                      disabled={saving}
                      onClick={() => handleAssign(p.id)}
                      className="w-full flex items-center gap-3 px-3 py-2.5 hover:bg-fluxi/5 transition-colors disabled:opacity-60"
                    >
                      <div className="flex-shrink-0 w-9 h-9 rounded bg-gray-100 overflow-hidden">
                        {p.images?.[0] ? (
                          <img src={p.images[0]} alt={p.name} className="w-full h-full object-cover" />
                        ) : null}
                      </div>
                      <div className="flex-1 min-w-0 text-left">
                        <p className="text-xs font-medium text-gray-900 truncate">{p.name}</p>
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-2 p-4 border-t border-gray-200">
          <button
            type="button"
            onClick={handleBack}
            disabled={cursor === 0 || saving}
            className="w-10 h-10 border border-gray-200 rounded-lg flex items-center justify-center hover:bg-gray-50 disabled:opacity-40"
            aria-label="Previous"
          >
            <ChevronLeft className="w-4 h-4 text-gray-500" />
          </button>
          <button
            type="button"
            onClick={handleSkip}
            disabled={saving}
            className="h-10 px-4 text-sm font-medium bg-gray-900 text-white rounded-lg hover:opacity-90 disabled:opacity-50"
          >
            {cursor < total - 1
              ? (dictionary["assign-modal-skip"]?.[language] ?? "Saltar")
              : (dictionary["assign-modal-finish"]?.[language] ?? "Terminar")}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AssignmentInboxModal;
